/* eslint-disable no-console */

import {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {readEndpoint} from 'redux-json-api'

import ImageViewer from './ImageViewer'

const getImageMimeType = extension => {
  const mimeMap = {
    bmp: 'image/bmp',
    gif: 'image/gif',
    heic: 'image/heic',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    svg: 'image/svg+xml',
    tif: 'image/tiff',
    tiff: 'image/tiff'
  }

  return mimeMap[(extension || '').toLowerCase()] || 'application/octet-stream'
}

const ImageViewerController = ({
  fileExtension,
  imageName,
  imageUrl,
  isModalOpen,
  onCloseModal,
  onDownloadClick,
  readEndpoint: readEndpointProp,
  uploadId
}) => {
  const [error, setError] = useState(null)
  const [imageSrc, setImageSrc] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isModalOpen) return

    const abortController = new AbortController()

    setIsLoading(true)
    setError(null)
    setImageSrc(null)

    const handleError = err => {
      if (err.name === 'AbortError') return

      console.error('Error fetching image:', err)

      let errorMessage = 'Unable to load image. Please try again or contact support if the issue persists.'

      if (err instanceof TypeError && !navigator.onLine) {
        errorMessage = 'Network error: Unable to fetch image. Please check your internet connection.'
      } else if (err.statusCode) {
        if (err.statusCode >= 500)
          errorMessage = `Server error: The server encountered an error (${err.statusCode}). Please try again later.`
        else if (err.statusCode === 404)
          errorMessage = 'Image not found. The requested file may have been moved or deleted.'
        else if (err.statusCode === 403)
          errorMessage = 'Access denied. You do not have permission to view this image.'
        else
          errorMessage = `Unable to load image. Error code: ${err.statusCode}`
      } else if (err.name === 'FileReaderError') {
        errorMessage = 'File read error: Unable to process the image file.'
      }

      setError(errorMessage)
      setIsLoading(false)
    }

    const handleSuccess = dataUrl => {
      setError(null)
      setImageSrc(dataUrl)
      setIsLoading(false)
    }

    if (imageUrl) {
      fetch(imageUrl, {signal: abortController.signal})
        .then(response => {
          if (!response.ok) {
            const fileError = new Error(`HTTP error! status: ${response.status}`)

            fileError.statusCode = response.status

            throw fileError
          }

          return response.blob()
        })
        .then(blob => {
          const fileReader = new FileReader()
          fileReader.onloadend = () => {
            if (abortController.signal.aborted) return

            if (typeof fileReader.result === 'string' &&
                fileReader.result.startsWith('data:')) {
              handleSuccess(fileReader.result)
            } else {
              const fileError = new Error(
                'Unexpected file format or empty result'
              )

              fileError.name = 'FileReaderError'
              handleError(fileError)
            }
          }
          fileReader.onerror = () => {
            const fileError = new Error('Failed to read file')
            fileError.name = 'FileReaderError'
            handleError(fileError)
          }
          fileReader.readAsDataURL(blob)
        })
        .catch(handleError)
    } else if (uploadId) {
      readEndpointProp(`uploads/${uploadId}?download=true`)
        .then(response => {
          if (abortController.signal.aborted) return

          const base64Data = response.body.data.attributes['decrypted-file']
          const mimeType = getImageMimeType(fileExtension)

          handleSuccess(`data:${mimeType};base64,${base64Data}`)
        })
        .catch(handleError)
    }

    return () => {
      abortController.abort()
    }
  }, [fileExtension, imageUrl, isModalOpen, readEndpointProp, uploadId])

  return (
    <ImageViewer
      error={error}
      imageName={imageName}
      imageSrc={imageSrc}
      isLoading={isLoading}
      isModalOpen={isModalOpen}
      onCloseModal={onCloseModal}
      onDownloadClick={onDownloadClick}
    />
  )
}

ImageViewerController.propTypes = {
  fileExtension: PropTypes.string,
  imageName: PropTypes.string,
  imageUrl: PropTypes.string,
  isModalOpen: PropTypes.bool,
  onDownloadClick: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func,
  readEndpoint: PropTypes.func.isRequired,
  uploadId: PropTypes.string
}

export default connect(null, {readEndpoint})(ImageViewerController)
